/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import isArray from 'lodash.isarray';
import isUndefined from 'lodash.isundefined';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {getAccountsByFileId, getlookupData} from '@ion/core/src/services/Connect';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {
  DeviceFiles,
  Field,
  Fields,
  GenericObject,
  Link,
  MaintainDeviceFileRequest,
  PlatformFileType
} from '@ion/core/src/types/entities';
import {DeviceFileGridResult} from '@ion/core/src/types/extended-entities';
import {isObjectEmpty} from '@ion/core/src/utils/ObjectUtil';
import {FormState} from '../hooks/useFormState';
import {getDeviceFileAppId} from './helpers';

export enum DeviceFileFormFields {
  AccountId = 'accountId',
  DeviceType = 'deviceType',
  OriginalDeviceFile = 'originalDeviceFile',
  FileType = 'fileType',
  FileUpload = 'fileUpload',
  FileName = 'name',
  Version = 'version',
  FromVersion = 'fromVersion',
  AppId = 'appId',
  VBus = 'vBus',
  KeyAlias = 'keyAlias',
  HostedApp = 'hostedApp',
  Description = 'description',
  ReleaseNoteAttached = 'releaseNoteAttached',
  ReleaseNotesUrl = 'releaseNotesUrl',
  ReleaseNotesName = 'releaseNotesName',
  FileAccessFor = 'fileAccessFor',
  SelectedAccounts = 'selectedAccounts',
  CoProcessor = 'co_processor',
  Modem = 'modem',
  ReportedName = 'reportedName',
  Status = 'status',
  UpdateAllAssignedDevices = 'updateAssignedDevices'
}

export const deviceFileTypes = ['Linux OPKG', 'EdgeApp Firmware'];

// defines the devicefile target type definition ...
export interface TargetType {
  name: DeviceFileFormFields,
  value: GenericObject;
  required?: boolean
}

export interface SubAccountSetStateType extends TargetType {
  subAccountsLists?: Link[];
}

export type SerializedArgs = FileUploadType & {
  state: FormState;
  account?: Link;
  hasAutoJobs?: boolean;
};

export type FileUploadType = {
  isFileUploaded?: boolean;
  isReleaseNotesUploaded?: boolean;
};

export const deviceFileEditors = 'devicefiles:editors';

export const loadLookupData = async function(field, id, type) {
  let url = field?.lookup?.urlMapping;
  if (type === 'regexLookup') {
    const x = field?.lookup?.urlMapping.split('/');
    const appId = x[x.length - 1];
    if (id?.length === 3 && id[0] === '0') {
      id = id.substr(id[1] === '0' ? 2 : 1);
    }
    url = field.lookup.urlMapping.replace(appId, id);
  };

  let results: any[];
  try {
    results = await getlookupData(url);
  } catch (err) {
    error('Lookup: Error occurred retrieving Lookup data');
  }

  return results;
};

const extractMatchdeRegexData = (matches, regex, matcherRecord) => {
  const returnObject = {};
  if (matches.index === regex.lastIndex) {
    regex.lastIndex += 1;
  }

  const keysToMatch = Object.keys(matcherRecord.matchLocations);
  for (let index = 0; index < keysToMatch.length; index += 1) {
    if (keysToMatch[index] !== 'fromKeyWord') {
      const key        = keysToMatch[index];
      const groupIndex = matcherRecord.matchLocations[key];
      const value      = matches[groupIndex];
      if (value) {
        returnObject['disable' + key] = true;
        returnObject[key + '1'] = false;
        returnObject[key] = value;
      }
    }
  }
  return returnObject;
};

export const handleRegexExpression = (fileType, fileSelected, inputFields) => {
  const regexMatchers = fileType?.fileRegExMatchers;
  const fileName      = fileSelected?.length > 0 ? fileSelected[0].filename : null;

  if (regexMatchers && fileName) {
    let match;
    for (let i = 0; i < regexMatchers.length; i++) {
      const regex = new RegExp(regexMatchers[i].regEx, 'gm');
      match       = regex.exec(fileName);
      if (match) {
        const appVersionData     = extractMatchdeRegexData(match, regex, regexMatchers[i]);
        let result               = {};
        const changedInputFields = [...inputFields].filter(_ => {
          if (appVersionData[_.name] && _.type === 'regex' && _.lookup) {
            loadLookupData(_, appVersionData[_.name], 'regexLookup');
          }
          return appVersionData[_.name] || _.type !== 'regex';
        });
        result = {inputFields: changedInputFields, ...appVersionData};
        return result;
      }
    }
  } else {
    const metaFields = {};
    inputFields.forEach(field => {
      if (field.type === 'lookup') {
        metaFields[field.name]          = [];
        metaFields[field.name+'lookup'] = [];
      } else {
        metaFields[field.name] = '';
      }
      metaFields['disable' + field.name] = false;
      metaFields.inputFields             = fileType?.inputFields;
    });
    return metaFields;
  }
};

/**
 * This retrieveCurrentAccount function provide a utility to retrieve the 'Only this Account' data.
 */
export const retrieveCurrentAccount = (currentAccount) => {
  const accountList = [];
  if (currentAccount?.length) {
    currentAccount.forEach(({fileMetaAccount: {href, title}}) => {
      accountList.push({id: entityId(href) ?? '', name: title ?? ''});
    });
  }
  return accountList;
};

export const retrieveAccounts = async function(
  selRowData,
  accountId,
  hasFileAccess = false
) {
  const fileID      = selRowData?.id;
  const accountList = [];
  const selAccList  = [];
  let response      = [];
  try {
    response = await getAccountsByFileId(fileID);
    if (response?.results?.length) {
      response.results.forEach(item => {
        const id = parseInt(item.fileMetaAccount.href.split('/').reverse()[0], 10);
        if (hasFileAccess && id === accountId) {
          selAccList.push({id, name: item.fileMetaAccount.title});
        } if (!hasFileAccess && id !== accountId) {
          accountList.push(id);
          selAccList.push({id, name: item.fileMetaAccount.title});
        }
      });
      return {response: response?.results, selectedList: selAccList};
    }
  } catch (err) {
    error('Lookup: Error occurred retrieving accounts');
  }
};

/**
   * Provides the initial state of the devicefile editor form, this state includes all named
   * editor fields and their requirements, as well as any initial values required, such
   * as the application for which the devicefile is to be created, or a reference to a devicefile,
   * in the case of edits.
   */
export const getInitialState = ({devicefile}: GenericObject) => {
  const required    = {required: true};
  const notRequired = {required: false};
  return deserialize(devicefile) || {
    [DeviceFileFormFields.AccountId]           : notRequired,
    [DeviceFileFormFields.DeviceType]          : required,
    [DeviceFileFormFields.FileType]            : required,
    [DeviceFileFormFields.FileUpload]          : required,
    [DeviceFileFormFields.FileName]            : required,
    [DeviceFileFormFields.Version]             : required,
    [DeviceFileFormFields.FromVersion]         : notRequired,
    [DeviceFileFormFields.AppId]               : notRequired,
    [DeviceFileFormFields.KeyAlias]            : notRequired,
    [DeviceFileFormFields.Description]         : {...required, value: ''},
    [DeviceFileFormFields.ReleaseNoteAttached] : notRequired,
    [DeviceFileFormFields.ReleaseNotesUrl]     : {...notRequired, value: ''},
    [DeviceFileFormFields.FileAccessFor]       : required,
    [DeviceFileFormFields.SelectedAccounts]    : notRequired,
    [DeviceFileFormFields.VBus]                : notRequired,
    [DeviceFileFormFields.CoProcessor]         : notRequired,
    [DeviceFileFormFields.Modem]               : notRequired,
    [DeviceFileFormFields.ReportedName]        : notRequired,
    [DeviceFileFormFields.Status]              : {notRequired},
    [DeviceFileFormFields.ReleaseNotesName]    : notRequired
  };
};

/**
 * Returns the proper state object to reset the dynamic fields' required state.
 * If you add a dynamic field, add it's default required status here.
 */
export const resetRequiredDynamicFieldsState = () => {
  const required    = {required: true};
  const notRequired = {required: false};
  return {
    [DeviceFileFormFields.Version]     : required,
    [DeviceFileFormFields.Description] : {...required, value: ''},
    [DeviceFileFormFields.AppId]       : notRequired,
    [DeviceFileFormFields.VBus]        : notRequired
  };
};

/**
   * Serializes the user's form fields and constructs the given add or edit device file request.
   */
export const serialize = ({
  state,
  account,
  isFileUploaded,
  isReleaseNotesUploaded,
  hasAutoJobs
}: SerializedArgs): MaintainDeviceFileRequest => {
  const {originalDeviceFile, ...formFields} = state;
  const deviceFile: DeviceFileGridResult    = originalDeviceFile?.value;

  account.rel       = 'account';
  const isEdit      = !!deviceFile;
  const addFormData = serializeAdd({formFields: serializeFormFields(formFields), account, isEdit, hasAutoJobs});

  const fileUpload = {
    isFileUploaded,
    isReleaseNotesUploaded
  };

  if (isEdit) {
    return serializeEdit(addFormData, deviceFile, fileUpload);
  }
  return addFormData;
};

// Device file form fields to be serialized or deserialized.
interface DeviceFileFormFieldsState {
  [DeviceFileFormFields.DeviceType]?: string;
  [DeviceFileFormFields.FileType]?: PlatformFileType | string;
  [DeviceFileFormFields.FileUpload]?: string;
  [DeviceFileFormFields.FileName]?: string;
  [DeviceFileFormFields.Version]?: string;
  [DeviceFileFormFields.FromVersion]?: string;
  [DeviceFileFormFields.VBus]?: Fields;
  [DeviceFileFormFields.HostedApp]?: Fields;
  [DeviceFileFormFields.AppId]?: Fields | string;
  [DeviceFileFormFields.KeyAlias]?: Fields;
  [DeviceFileFormFields.Description]?: string;
  [DeviceFileFormFields.ReleaseNoteAttached]?: boolean;
  [DeviceFileFormFields.ReleaseNotesUrl]?: string;
  [DeviceFileFormFields.FileAccessFor]?: boolean;
  [DeviceFileFormFields.SelectedAccounts]?: number[];
  [DeviceFileFormFields.CoProcessor]?: Fields;
  [DeviceFileFormFields.Modem]?: Fields;
  [DeviceFileFormFields.ReportedName]?: string;
  [DeviceFileFormFields.Status]?: string;
  [DeviceFileFormFields.UpdateAllAssignedDevices]?: boolean;
  [DeviceFileFormFields.ReleaseNotesName]?: string;
}

const getCollectionValue = (collection) => {
  if (!isObjectEmpty(collection) || collection?.length) return collection;
};

export const hasMultipleAppids = (vBus: Fields | Field): boolean => Object.keys(vBus).length === 1 ||
(isArray(vBus) && vBus.length > 1);

const getArrayValue = (fieldValue) => {
  return Array.isArray(fieldValue) ? fieldValue : [fieldValue];
};

type SerializeAddParams = {
  formFields: DeviceFileFormFieldsState;
  account: Link;
  isEdit: boolean;
  hasAutoJobs?: boolean;
}

/**
 * Prepares the request for adding a device file using the user's entered form fields to populate.
 */
const serializeAdd = ({
  formFields : {
    fileUpload,
    description,
    fileAccessFor,
    releaseNotesUrl,
    releaseNoteAttached,
    releaseNotesName,
    fileType,
    deviceType,
    version,
    co_processor,
    modem,
    reportedName,
    selectedAccounts,
    vBus,
    hostedApp,
    fromVersion,
    appId,
    keyAlias,
    status,
    updateAssignedDevices,
    name: fileName
  },
  account,
  isEdit,
  hasAutoJobs
}: SerializeAddParams): MaintainDeviceFileRequest => {
  const platformFileType = fileType as PlatformFileType;
  const title            = isEdit ? 'validationMetaData' : 'metadata';
  return {
    name                : fileName || (typeof fileUpload !== 'string' ? fileUpload?.[0]?.filename : fileUpload),
    description         : description,
    status              : status,
    accountId           : entityId(account?.href),
    inheritable         : fileAccessFor,
    inheritableAccounts : selectedAccounts?.length ? selectedAccounts.map(e => entityId(e)) : null,
    [title]             : {
      deviceTypeId   : platformFileType?.deviceTypeId,
      deviceTypeName : deviceType,
      fileTypeId     : platformFileType?.id,
      fileTypeName   : platformFileType?.name,
      platformId     : platformFileType?.platformId,
      platformName   : platformFileType?.platformName,
      releaseNotesName,
      fields         : {
        appId,
        fromVersion,
        keyAlias,
        reportedName,
        co_processor : co_processor ? getArrayValue(co_processor) : null,
        modem        : modem ? getArrayValue(modem) : null,
        hostedApp    : hostedApp ? getArrayValue(hostedApp) : null,
        vBus         : getCollectionValue(vBus),
        version      : getCollectionValue(version),
        ...(hasAutoJobs && {updateAssignedDevices})
      }
    },
    releaseNotesUrl     : releaseNotesUrl?.length ? releaseNotesUrl : '',
    releaseNoteAttached : releaseNoteAttached || false
  } as MaintainDeviceFileRequest;
};

/**
 * Serializes the user's entered form fields to be used to create the add / edit request.
 */
const serializeFormFields = (formFields: FormState): DeviceFileFormFieldsState => {
  const serialized = {};
  for (const [key, {value}] of Object.entries(formFields)) {
    switch (key) {
      case DeviceFileFormFields.FileAccessFor :
        serialized[DeviceFileFormFields.FileAccessFor] = (value === 2);
        break;
      default :
        if (value || value === false) {
          serialized[key] = value;
        }
        break;
    }
  }
  return serialized;
};

/**
 * Returns the serialized form specific to when a user edit's a device file.
 * Includes any persisting device file fields while allowing for user's new fields to take precedence to be
 * properly saved.
 */
const serializeEdit = (
  formData: MaintainDeviceFileRequest,
  deviceFile: DeviceFileGridResult,
  fileUpload: FileUploadType
): MaintainDeviceFileRequest => {
  return {
    ...deviceFile,
    validationMetaData : {
      ...formData?.validationMetaData,
      deviceTypeId             : deviceFile?.validationMetaData?.deviceTypeId,
      fileTypeId               : deviceFile?.validationMetaData?.fileTypeId,
      fileTypeName             : deviceFile?.validationMetaData?.fileTypeName,
      platformId               : deviceFile?.validationMetaData?.platformId,
      platformName             : deviceFile?.validationMetaData?.platformName,
      updateDataFileAttachment : fileUpload?.isFileUploaded,
      releaseNoteAttached      : formData?.releaseNoteAttached,
      releaseNotesName         : formData?.validationMetaData?.releaseNotesName,
      ...(deviceFile?.validationMetaData?.autoJobId && {autoJobId: deviceFile?.validationMetaData?.autoJobId})
    },
    updateReleaseNoteAttachment : fileUpload?.isReleaseNotesUploaded,
    validationStatus            : deviceFile?.validationStatus || {status: 'INVALID'},
    name                        : formData?.name,
    inheritable                 : formData?.inheritable,
    inheritableAccounts         : formData?.inheritableAccounts,
    description                 : formData?.description,
    status                      : formData?.status,
    releaseNotesUrl             : formData?.releaseNotesUrl
  } as MaintainDeviceFileRequest;
};

/**
 * Gets the appId whether it's in an array or just plain.
 */
export const getAppId = (deviceFile: DeviceFileGridResult, withDescription: boolean = false): string | null => {
  const deviceFileAppId = deviceFile?.validationMetaData?.fields?.appId;
  if (deviceFileAppId) {
    if (Array.isArray(deviceFileAppId)) {
      if (deviceFileAppId.length > 0) {
        return getDeviceFileAppId(deviceFileAppId, withDescription);
      }
      return '';
    }
    return deviceFileAppId;
  }
  return null;
};

export const getAccounts = async(devicefile, account) => {
  const accountsList = await retrieveAccounts(devicefile, entityId(account));
  return accountsList;
};

export const getInheritable = ({inheritable}: DeviceFiles): string => {
  if (!isUndefined(inheritable)) {
    return translate('devicefiles:editors.inheritable.yes');
  }
  return '';
};

export const platFormNames = ['LMU PEG2'];

export const checkPlatFormNames = (platFormName) => platFormNames.indexOf(platFormName);

/**
   * @deserialize
   *
   * Deserialized a DM Device File into an object which can be consumed by the DeviceFile Editor Form Fields
   * and used to pre-populated their respective values.
   *
   * @param {DeviceFile} devicefile An optional DM devicefile instance that is to be deserialized for the form.
   */
export const deserialize = (devicefile: DeviceFileGridResult): FormState => {
  if (devicefile) {
    return {
      [DeviceFileFormFields.OriginalDeviceFile] : {
        name  : DeviceFileFormFields.OriginalDeviceFile,
        value : devicefile
      },
      [DeviceFileFormFields.DeviceType] : {
        name     : DeviceFileFormFields.DeviceType,
        value    : devicefile?.validationMetaData?.deviceTypeName,
        required : false
      },
      [DeviceFileFormFields.FileType] : {
        name     : DeviceFileFormFields.FileType,
        value    : devicefile?.validationMetaData?.fileTypeName,
        required : false
      },
      [DeviceFileFormFields.FileUpload] : {
        name     : DeviceFileFormFields.FileUpload,
        value    : devicefile?.name,
        required : true
      },
      [DeviceFileFormFields.FileName] : {
        name     : DeviceFileFormFields.FileName,
        value    : devicefile?.name,
        required : true
      },
      [DeviceFileFormFields.Version] : {
        name     : DeviceFileFormFields.Version,
        value    : devicefile?.fileVersionUI,
        required : false
      },
      [DeviceFileFormFields.FromVersion] : {
        name     : DeviceFileFormFields.FromVersion,
        value    : devicefile?.validationMetaData?.fields?.fromVersion,
        required : false
      },
      [DeviceFileFormFields.AppId] : {
        name     : DeviceFileFormFields.AppId,
        value    : devicefile?.validationMetaData?.fields?.appId,
        required : false
      },
      [DeviceFileFormFields.Modem] : {
        name     : DeviceFileFormFields.Modem,
        value    : devicefile?.validationMetaData?.fields?.modem,
        required : false
      },
      [DeviceFileFormFields.CoProcessor] : {
        name     : DeviceFileFormFields.CoProcessor,
        value    : devicefile?.validationMetaData?.fields?.co_processor,
        required : false
      },
      [DeviceFileFormFields.VBus] : {
        name     : DeviceFileFormFields.VBus,
        value    : devicefile?.validationMetaData?.fields?.vBus,
        required : false
      },
      [DeviceFileFormFields.KeyAlias] : {
        name     : DeviceFileFormFields.KeyAlias,
        value    : devicefile?.validationMetaData?.fields?.keyAlias,
        required : false
      },
      [DeviceFileFormFields.HostedApp] : {
        name     : DeviceFileFormFields.HostedApp,
        value    : devicefile?.validationMetaData?.fields?.hostedApp,
        required : false
      },
      [DeviceFileFormFields.Description] : {
        name     : DeviceFileFormFields.Description,
        value    : devicefile?.description,
        required : true
      },
      [DeviceFileFormFields.ReleaseNoteAttached] : {
        name     : DeviceFileFormFields.ReleaseNoteAttached,
        value    : devicefile?.validationMetaData?.releaseNoteAttached,
        required : false
      },
      [DeviceFileFormFields.ReleaseNotesUrl] : {
        name     : DeviceFileFormFields.ReleaseNotesUrl,
        value    : devicefile?.validationMetaData?.releaseNotesUrl,
        required : false
      },
      [DeviceFileFormFields.FileAccessFor] : {
        name     : DeviceFileFormFields.FileAccessFor,
        value    : devicefile?.fileAccessFor,
        required : false
      },
      [DeviceFileFormFields.SelectedAccounts] : {
        name     : DeviceFileFormFields.SelectedAccounts,
        value    : devicefile?.selectedAccounts,
        required : false
      },
      [DeviceFileFormFields.ReportedName] : {
        name     : DeviceFileFormFields.ReportedName,
        value    : devicefile?.validationMetaData?.fields?.reportedName,
        required : false
      },
      [DeviceFileFormFields.Status] : {
        name     : DeviceFileFormFields.Status,
        value    : devicefile?.status,
        required : false
      },
      [DeviceFileFormFields.ReleaseNotesName] : {
        name     : DeviceFileFormFields.ReleaseNotesName,
        value    : devicefile?.validationMetaData?.releaseNotesName,
        required : false
      },
      [DeviceFileFormFields.UpdateAllAssignedDevices] : {
        name     : DeviceFileFormFields.UpdateAllAssignedDevices,
        value    : devicefile?.validationMetaData?.fields?.updateAssignedDevices,
        required : false
      }
    };
  }
};
